import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface Option {
  value: string
  label: string
  latitude?: number
  longitude?: number
}

interface MyMultiSelectProps {
  label: string
  options: Option[]
  value: string[]
  onChange: (value: string[]) => void
  error?: string
  className?: string
  readOnly?: boolean
  icon?: React.ReactNode
  maxVisibleOptions?: number
  required?: boolean
  hideLabel?: boolean
}

const MyMultiSelect: React.FC<MyMultiSelectProps> = ({
  label,
  options,
  value = [],
  onChange,
  error,
  className = '',
  readOnly = false,
  icon,
  maxVisibleOptions = 3,
  required = false,
  hideLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const uniqueId = useRef(`select-${Math.random().toString(36).substr(2, 9)}`)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleOptionClick = (optionValue: string) => {
    setSearchTerm('')
    if (value.includes(optionValue)) {
      onChange(value.filter(val => val !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
    setSearchTerm('')
  }

  const handleRemoveOption = (optionValue: string) => {
    onChange(value.filter(val => val !== optionValue))
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getBackgroundColor = () => {
    if (error) return 'bg-redlight'
    if (required && value.length === 0) return 'bg-redlight rounded-md'
    if (value.length > 0) return 'bg-transparent'
    return ''
  }

  return (
    <div
      className={`relative w-full bg-grisclair border-0 rounded-md ${className}`}
      ref={containerRef}
    >
      <div
        className={`flex flex-col w-full text-sm ${
          isOpen || value.length > 0 ? 'focused' : ''
        } ${getBackgroundColor()}`}
      >
        {(!hideLabel || value.length === 0) && (
          <label
            htmlFor={uniqueId.current}
            className={`transform transition-all duration-300 z-0 cursor-pointer ${
              value.length > 0
                ? 'absolute -translate-y-4 left-3 ml-0 w-auto font-semibold text-xs text-white  rounded-t-md px-2 py-0 shadow-sm -z-12  hover:bg-anthracite hover:border-anthracite'
                : `absolute inset-0 flex items-center ${
                    icon ? 'pl-10' : 'pl-2'
                  } text-gray-400 text-sm`
            } ${error ? 'text-redpink' : ''}`}
            onClick={() => {
              if (value.length === 0) {
                setIsOpen(true)
                inputRef.current?.focus()
              }
            }}
          >
            {label}
          </label>
        )}
        <div
          className='flex justify-between items-center flex-nowrap cursor-pointer w-full rounded-md overflow-x-auto hover:bg-grisclair2'
          onClick={() => setIsOpen(!isOpen)}
        >
          {icon && (
            <div className='h-full pl-3 pr-0.5 text-anthracite py-2 sm:py-3.5 bg-transparent rounded-l-md'>
              {icon}
            </div>
          )}
          <div className='flex flex-wrap flex-grow'>
            {value.length > maxVisibleOptions ? (
              <div className='flex items-center pl-2'>
                <span>
                  {`${value.length}`} {t('selected-plural')}
                </span>
                <button
                  type='button'
                  className='ml-2 cursor-pointer bg-anthracite text-white px-1 rounded-md transition-all duration-300 hover:bg-redpink'
                  onClick={e => {
                    e.stopPropagation()
                    onChange([])
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              value.map(val => (
                <div
                  key={val}
                  className='flex items-center bg-transparent text-anthracite px-0 py-0 rounded-md ml-2'
                >
                  <span className='mx-1'>
                    {options.find(option => option.value === val)?.label}
                  </span>
                  <button
                    type='button'
                    className='cursor-pointer bg-anthracite text-white px-1 rounded-md transition-all duration-300 hover:bg-redpink'
                    onClick={e => {
                      e.stopPropagation()
                      handleRemoveOption(val)
                    }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {isOpen && (
        <div className='absolute z-40 w-full'>
          <input
            type='text'
            ref={inputRef}
            value={searchTerm}
            placeholder={t('fast-search')}
            onChange={e => !readOnly && setSearchTerm(e.target.value)}
            className='w-full bg-anthraciteLight  px-5 py-1 text-white border-none outline-none transition-all duration-300 focus:border-none focus:outline-none placeholder-grisclair2/50'
            readOnly={readOnly}
          />
          <ul
            className='absolute left-0 w-full bg-anthracite text-white text-sm xl:text-base max-h-60 overflow-y-auto z-10 border-0 min-h-20 transition-all duration-300 rounded-b-md
  scrollbar-thin scrollbar-thumb-redpink scrollbar-track-anthraciteLight 
  hover:scrollbar-thumb-redpink/80
  [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:bg-anthraciteLight/20
  [&::-webkit-scrollbar-thumb]:bg-redpink
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-track]:rounded-full
  [&::-webkit-scrollbar-thumb]:hover:bg-redpink/80
'
          >
            {[
              ...options.filter(option => value.includes(option.value)),
              ...options.filter(option => !value.includes(option.value)),
            ]
              .filter(option =>
                option.label?.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .map(option => (
                <li
                  key={option.value}
                  className={`px-5 py-1 cursor-pointer border-b border-dashed border-anthraciteLight ${
                    value.includes(option.value)
                      ? 'bg-anthracite text-white'
                      : ''
                  } hover:bg-anthraciteLight flex items-center`}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <div className='relative flex items-center'>
                    <input
                      type='checkbox'
                      checked={value.includes(option.value)}
                      onChange={() => handleOptionClick(option.value)}
                      className={`
        mr-3 cursor-pointer
        w-4 h-4 
        rounded
        border border-grisfonce
        transition-all duration-300
        appearance-none
        ${
          value.includes(option.value)
            ? 'bg-redpink border-redpink'
            : 'bg-transparent hover:border-redpink'
        }
        focus:outline-none focus:ring-2 focus:ring-redpink focus:ring-offset-0
      `}
                    />
                    {value.includes(option.value) && (
                      <svg
                        className='absolute left-0.5 top-0.5 w-3 h-3 text-white pointer-events-none'
                        viewBox='0 0 12 12'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M2 6L5 9L10 3'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                  <label className='cursor-pointer hover:pl-1 transition-all'>
                    {option.label}
                  </label>
                </li>
              ))}
          </ul>
        </div>
      )}
      {error && (
        <span className='text-redpink mt-0 pl-1 text-xs text-left'>
          {error}
        </span>
      )}
    </div>
  )
}

export default MyMultiSelect
